import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getServices, createService, updateService, deleteService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useConfirm } from '../../components/ConfirmDialog';
import PageTour from '../../components/tenant/PageTour';
import MoneyInput, { formatMoneyBRL } from '../../components/MoneyInput';
import PercentInput from '../../components/PercentInput';
import Toggle from '../../components/Toggle';

const COLORS = ['#2563eb','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#06b6d4','#84cc16'];

const emptyForm = {
  name:'', description:'', duration_minutes:60, price:0, color:'#2563eb',
  requires_deposit: false, deposit_type: 'fixed', deposit_value: 0,
};

// Steps do tour guiado — textos curtos e focados por elemento.
const TOUR_STEPS = [
  {
    selector: '[data-tour="services-new-btn"]',
    title: 'Cadastre seus serviços',
    content: 'Clique aqui pra criar cada serviço que você oferece — nome, duração e preço. Ex: "Corte · 30min · R$ 40".',
    placement: 'bottom',
  },
  {
    selector: '[data-tour="services-list"]',
    title: 'Cada card é um serviço',
    content: 'A duração é usada pra calcular automaticamente os horários livres. Cliente nunca marca dois atendimentos no mesmo slot.',
    placement: 'top',
  },
  {
    title: '💳 Dica: cobrar sinal',
    content: 'No modal de criar/editar serviço, ative "Cobrar sinal via PIX" pra exigir um adiantamento do cliente. Reduz falta em até 60%.',
  },
];

export default function TenantServices() {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [services, setServices] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [modal,    setModal]    = useState(null); // null | 'create' | {id,...}
  const [form,     setForm]     = useState(emptyForm);
  const [saving,   setSaving]   = useState(false);

  useEffect(() => {
    if (!hasPermission('MANAGE_TENANT_SETTINGS')) { navigate('/app', { replace: true }); }
  }, [hasPermission, navigate]);

  const load = () => {
    setLoading(true);
    getServices().then(r => setServices(r.data.services || [])).catch(() => toast.error('Erro ao carregar serviços')).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const openCreate = () => { setForm(emptyForm); setModal('create'); };
  const openEdit   = svc => { setForm({
    name: svc.name,
    description: svc.description || '',
    duration_minutes: svc.duration_minutes,
    price: svc.price,
    color: svc.color || '#2563eb',
    requires_deposit: !!svc.requires_deposit,
    deposit_type: svc.deposit_type || 'fixed',
    deposit_value: svc.deposit_value != null ? Number(svc.deposit_value) : 0,
  }); setModal(svc); };
  const closeModal = () => setModal(null);

  const onChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const onSave = async e => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Informe o nome do serviço'); return; }
    if (Number(form.duration_minutes) < 5) { toast.error('Duração mínima: 5 minutos'); return; }
    if (Number(form.price) < 0) { toast.error('Preço não pode ser negativo'); return; }
    setSaving(true);
    try {
      const payload = { ...form, duration_minutes: Number(form.duration_minutes), price: Number(form.price) };
      if (modal === 'create') {
        await createService(payload);
        toast.success('Serviço criado');
      } else {
        await updateService(modal.id, payload);
        toast.success('Serviço atualizado');
      }
      closeModal();
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  const onDelete = async id => {
    const ok = await confirm({
      title: 'Remover serviço?',
      message: 'Agendamentos futuros vinculados a este serviço perderão a referência ao serviço, mas NÃO serão cancelados. Você pode reatribuir manualmente se necessário.',
      confirmText: 'Remover',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await deleteService(id);
      toast.success('Serviço removido');
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Erro ao remover'); }
  };

  const fmtPrice = v => formatMoneyBRL(v);

  return (
    <div className="page-body">
      <div className="page-header-row">
        <div className="page-header">
          <h2>Catálogo de Serviços</h2>
          <p>Serviços disponíveis para agendamento</p>
          <div style={{ marginTop: 8 }}>
            <PageTour tourId="services" steps={TOUR_STEPS} />
          </div>
        </div>
        <button className="btn btn-primary" data-tour="services-new-btn" onClick={openCreate}>+ Novo Serviço</button>
      </div>

      {loading ? (
        <div className="spinner" style={{ margin:'40px auto', display:'block' }} />
      ) : !services.length ? (
        <div className="empty-state">
          <div className="empty-icon">🛎️</div>
          <h3>Nenhum serviço cadastrado</h3>
          <p>Cadastre serviços para exibir na sua página de agendamento online.</p>
          <button className="btn btn-primary" style={{ marginTop:12 }} onClick={openCreate}>Criar primeiro serviço</button>
        </div>
      ) : (
        <div data-tour="services-list" style={{ display:'flex', flexDirection:'column', gap:12 }}>
          {services.map(svc => (
            <div key={svc.id} className="mobile-card" style={{ borderLeft:`4px solid ${svc.color||'#2563eb'}`, cursor:'default' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12 }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:700, fontSize:15, color:'var(--gray-900)', marginBottom:2 }}>{svc.name}</div>
                  {svc.description && <div style={{ fontSize:12, color:'var(--gray-500)', marginBottom:6 }}>{svc.description}</div>}
                  <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
                    <span style={{ fontSize:13, color:'var(--gray-600)' }}>⏱ {svc.duration_minutes} min</span>
                    <span style={{ fontSize:13, color:'var(--gray-600)' }}>💰 {fmtPrice(svc.price)}</span>
                  </div>
                </div>
                <div style={{ display:'flex', gap:8, flexShrink:0 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => openEdit(svc)}>Editar</button>
                  <button className="btn btn-danger btn-sm" onClick={() => onDelete(svc.id)}>Remover</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth:480 }}>
            <div className="modal-header">
              <h3>{modal === 'create' ? 'Novo Serviço' : 'Editar Serviço'}</h3>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>
            <form onSubmit={onSave}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Nome do serviço *</label>
                  <input name="name" value={form.name} onChange={onChange} required placeholder="Ex: Corte de cabelo" />
                </div>
                <div className="form-group">
                  <label>Descrição</label>
                  <textarea name="description" value={form.description} onChange={onChange} rows={2} placeholder="Descrição opcional do serviço" />
                </div>
                <div className="grid-2">
                  <div className="form-group">
                    <label>Duração (minutos) *</label>
                    <input name="duration_minutes" type="number" min={5} max={480} value={form.duration_minutes} onChange={onChange} required />
                  </div>
                  <div className="form-group">
                    <label>Preço (R$)</label>
                    <MoneyInput
                      value={form.price}
                      onChange={v => setForm(f => ({ ...f, price: v }))}
                      min={0}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Cor</label>
                  <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:4 }}>
                    {COLORS.map(c => (
                      <button key={c} type="button"
                        onClick={() => setForm(f => ({ ...f, color: c }))}
                        style={{
                          width:28, height:28, borderRadius:'50%', background:c, border:'none', cursor:'pointer',
                          outline: form.color === c ? '3px solid var(--gray-700)' : '2px solid transparent',
                          outlineOffset:2,
                        }}
                      />
                    ))}
                  </div>
                </div>

                {/* ── Sinal via PIX (opt-in) ──────────────────────────────── */}
                <div className="form-group" style={{
                  background: 'var(--gray-50)', borderRadius: 8,
                  padding: '12px 14px', marginTop: 4, border: '1px solid var(--gray-200)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                    <div>
                      <label style={{ display: 'block', fontWeight: 600, marginBottom: 2 }}>
                        💳 Cobrar sinal via PIX
                      </label>
                      <small style={{ color: 'var(--gray-500)', fontSize: 12 }}>
                        Cliente paga parte do valor na hora do agendamento pra confirmar.
                      </small>
                    </div>
                    <Toggle
                      checked={!!form.requires_deposit}
                      onChange={() => setForm(f => ({ ...f, requires_deposit: !f.requires_deposit }))}
                    />
                  </div>

                  {form.requires_deposit && (
                    <div className="grid-2" style={{ marginTop: 12 }}>
                      <div>
                        <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' }}>
                          Como cobrar
                        </label>
                        <select
                          value={form.deposit_type}
                          onChange={e => setForm(f => ({ ...f, deposit_type: e.target.value }))}
                        >
                          <option value="fixed">Valor fixo (R$)</option>
                          <option value="percent">Porcentagem do serviço (%)</option>
                        </select>
                      </div>
                      <div>
                        <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' }}>
                          {form.deposit_type === 'percent' ? 'Porcentagem' : 'Valor do sinal'}
                        </label>
                        {form.deposit_type === 'percent' ? (
                          <PercentInput
                            value={form.deposit_value}
                            onChange={v => setForm(f => ({ ...f, deposit_value: v }))}
                            min={1}
                            max={100}
                          />
                        ) : (
                          <MoneyInput
                            value={form.deposit_value}
                            onChange={v => setForm(f => ({ ...f, deposit_value: v }))}
                            min={0}
                          />
                        )}
                      </div>
                      <div style={{ gridColumn: '1 / -1', fontSize: 12, color: 'var(--gray-500)', marginTop: -4 }}>
                        {form.deposit_type === 'percent' && Number(form.price) > 0 && Number(form.deposit_value) > 0 && (
                          <>Ex: {formatMoneyBRL(Number(form.price) * Number(form.deposit_value) / 100)} sobre R$ {Number(form.price).toFixed(2).replace('.', ',')}</>
                        )}
                        {form.deposit_type === 'fixed' && Number(form.deposit_value) > 0 && (
                          <>Cliente pagará {formatMoneyBRL(form.deposit_value)} ao agendar</>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancelar</button>
                <button type="submit" className={`btn btn-primary${saving?' btn-loading':''}`} disabled={saving}>{saving ? '' : 'Salvar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
